import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import Icon from '../../../components/Icon'
import Loader from '../../../components/Loader'
import colors from '../../../constants/colors'
import styles from '../../../constants/styles'
import useHelpContent from '../../../hooks/useHelpContent'
import useSlug from '../../../hooks/useSlug'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import EmbeddedVideo from '../../embeddedvideo/EmbeddedVideo'
import {
    cinemaSelector,
    currentVideoSelector,
    fetchCinema,
    fetchVideos,
    limitVideosSelector
} from '../cinemaSlice'
import Sidebar from '../components/Sidebar'

interface StyledVideoProps {
    isFullScreen?: boolean
    width?: number
    height?: number
}

const S = {
    Wrapper: styled.div<{background: string}>`
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        padding-right: ${styles.sidebarWidth + 20}px;
        padding-left: 20px;
        background: url(${({ background }) => background}) no-repeat;
        background-size: cover;
        background-position: center;
        @media (max-width: 1366px) {
            padding-right: ${styles.smallerSidebarWidth + 20}px;
        }
    `,
    VideoCenter: styled.div`

    `,
    VideoPlayerWrapper: styled.div<StyledVideoProps>`
        width: ${({ width }) => width}px;
        height: ${({ height }) => height}px;
        background-color: grey;
        ${({ isFullScreen }) => isFullScreen && css`
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw!important;
            height: calc(100vh - 46px)!important;
            z-index: 9999;
        `} 
        
    `,
    VideoBottomBar: styled.div<StyledVideoProps>`
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: ${({ width }) => width}px;
        background-color: white;
        padding: 10px 20px;
        border-top: 1px solid ${colors.gray3};
        ${({ isFullScreen }) => isFullScreen && css`
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100vw!important;
            padding-left: 80px;
            z-index: 9999;
        `}
    `,
    Title: styled.h3`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 3px;
    `,
    Description: styled.p`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 14px;
        color: ${colors.gray6};
        margin: 0;
    `
}

const Cinema = () => {
    const dispatch = useDispatch()
    const eventSlug = useSlug('eventSlug')
    const cinemaSlug = useSlug('cinemaSlug')
    const { data: cinema, requestStatus } = useSelector(cinemaSelector(cinemaSlug))
    const currentVideo = useSelector(currentVideoSelector)
    const limit = useSelector(limitVideosSelector)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const hasData = !!cinema

    useHelpContent(cinema?.meta.help_content)

    useEffect(() => {
        if (!hasData) {
            dispatch(fetchCinema({ eventSlug, cinemaSlug }))
        }
    }, [eventSlug, cinemaSlug, dispatch, hasData])

    useEffect(() => {
        dispatch(fetchVideos({ eventSlug, cinemaSlug, limit }))
    }, [eventSlug, cinemaSlug, limit, dispatch])

    const { width, height } = useWindowDimensions()
    let videoHeight = 0.75 * (height - styles.topNavHeight)
    let videoWidth = (videoHeight * 16) / 9
    if (videoWidth > (width - 320 - 500) || videoWidth > 1000) {
        // Max video width: 1000px
        // width of sidebar: 320px, 250px left-right side for sponsor -> 500px
        videoWidth = Math.min(width - 320 - 500, 1000)
        videoHeight = (videoWidth * 9) / 16
    }

    if (requestStatus !== 'succeeded') {
        return <Loader />
    }

    if (!currentVideo) {
        return <p>No video available</p>
    }

    return (
        <S.Wrapper background={cinema.meta.image}>
            <S.VideoCenter>
                <S.VideoPlayerWrapper width={videoWidth} height={videoHeight} isFullScreen={isFullScreen}>
                    <EmbeddedVideo url={currentVideo.embedded_url} />
                </S.VideoPlayerWrapper>
                <S.VideoBottomBar width={videoWidth} isFullScreen={isFullScreen}>
                    <div style={{ width: '75%' }}>
                        <S.Title>{currentVideo.title}</S.Title>
                        {!isFullScreen && <S.Description>{currentVideo.subtitle}</S.Description>}
                    </div>
                    <Icon
                        icon={isFullScreen ? 'fullscreenExit' : 'fullscreen'}
                        onClick={() => setIsFullScreen(!isFullScreen)}
                    />
                </S.VideoBottomBar>
            </S.VideoCenter>
            <Sidebar />
        </S.Wrapper>
    )
}

export default Cinema
